import { Buffer } from 'buffer';

import MsgReader from '@kenjiuno/msgreader';
import { decode, ReadedEmlJson, readEml } from 'eml-parse-js';
import { lookup as lookupMimeType } from 'mime-types';
import { createMimeMessage } from 'mimetext/browser';
import {
  BehaviorSubject,
  catchError,
  concatMap,
  delay,
  finalize,
  forkJoin,
  from,
  map,
  mergeMap,
  NEVER,
  Observable,
  of,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
  toArray,
} from 'rxjs';

import { LoggerService } from '../service/logger.service';
import { cleanAndTruncateFileName, cleanupFilename, convertFileToBuffer, MAX_FILENAME_LENGTH } from './file-utils';

export interface ExtendedReadedEmlJson extends ReadedEmlJson {
  receivedDate?: Date;
}

export interface OutlookContentResult<A, B> {
  detail: A;
  value: B;
}

/**
 * Enhanced version of the `Word.RequestContext` type.
 * Word API doesn't have concept of an ID or a name for the file.
 *
 * the URL is unique for the file and can be used as an ID.
 * and the filename is the last part of the URL.
 *
 *  (only have values if the document is saved)
 */
export type WordRequestContext = Word.RequestContext & {
  // URL of the document in OneDrive or SharePoint
  url: string;
  // Name of the document
  fileName: string;
};

export type OutlookItemType = Office.Item &
  Office.ItemCompose &
  Office.ItemRead &
  Office.Message &
  Office.MessageCompose &
  Office.MessageRead &
  Office.Appointment &
  Office.AppointmentCompose &
  Office.AppointmentRead;

/**
 * Gets the attachment content as an observable.
 * @param item The Outlook item to get the attachment from.
 * @param att The attachment details.
 * @param logger The logger service.
 * @returns An observable that emits the attachment content.
 */
export function getAttachmentContentAsyncObservable(
  item: OutlookItemType | Office.LoadedMessageRead,
  att: Office.AttachmentDetails,
  logger: LoggerService
): Observable<OutlookContentResult<Office.AttachmentDetails, Office.AttachmentContent>> {
  return new Observable((observer) => {
    item.getAttachmentContentAsync(att.id, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        // outlook sometimes does not report correct success.. so lets test.
        if (result.value.content === '' && att.size > 0) {
          observer.error(new Error(`Failed to get attachment content.  Outlook returned empty content for ${att.size} byte attachment`));
        } else {
          observer.next({ detail: att, value: stripUnsentFromAttachment(att, result.value) });
          observer.complete();
        }
      } else {
        logger.error('[OfficeUtils] API error:', result.error);
        observer.error(new Error('Failed to get attachment content'));
      }
    });
  });
}

const UNSENT_HEADER = /^X-Unsent:[^\r\n]*(?:\r?\n[ \t][^\r\n]*)*\r?\n/im;

/** Removes X-Unsent from a raw MIME string so Outlook opens it as a received message, not a draft. */
export function stripUnsentHeaderFromText(eml: string): string {
  const headerEnd = eml.search(/\r?\n\r?\n/);
  if (headerEnd < 0) return eml;

  // +2 keeps the last header line's terminator inside the header block
  const headers = eml.slice(0, headerEnd + 2);
  const cleaned = headers.replace(UNSENT_HEADER, '');
  return cleaned === headers ? eml : cleaned + eml.slice(headerEnd + 2);
}

/** Base64 variant of {@link stripUnsentHeaderFromText}. */
export function stripUnsentHeader(base64: string): string {
  // latin1 round-trips bytes losslessly, so attachments and non-UTF-8 content stay intact
  const eml = Buffer.from(base64, 'base64').toString('latin1');
  const cleaned = stripUnsentHeaderFromText(eml);
  return cleaned === eml ? base64 : Buffer.from(cleaned, 'latin1').toString('base64');
}

function stripUnsentFromAttachment(att: Office.AttachmentDetails, content: Office.AttachmentContent): Office.AttachmentContent {
  const { Eml: eml, Base64: base64 } = Office.MailboxEnums.AttachmentContentFormat;
  // Outlook returns attached emails (item attachments) as plain MIME text, not base64
  if (content.format === eml) {
    return { ...content, content: stripUnsentHeaderFromText(content.content) };
  }
  if (content.format === base64 && /\.eml$/i.test(att.name)) {
    return { ...content, content: stripUnsentHeader(content.content) };
  }
  return content;
}

/**
 * Gets the file content as a file object.
 * @param item The Outlook item to get the file from.
 * @param logger The logger service.
 * @returns An observable that emits the file content.
 */
export function getAsFileAsyncObservable(
  item: OutlookItemType | Office.LoadedMessageRead,
  logger: LoggerService
): Observable<OutlookContentResult<string, string>> {
  return new Observable((observer) => {
    item.getAsFileAsync({}, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        observer.next({ detail: item.itemId, value: stripUnsentHeader(result.value) });
        observer.complete();
      } else {
        logger.error('[OfficeUtils] Failed to get file:', result.error);
        observer.error(new Error('Failed to get file'));
      }
    });
  });
}

/**
 * Gets the file properties as an observable.
 * @returns An observable that emits the file properties.
 */
export function getFilePropertiesObservable() {
  return createObservableFromAsyncResult<Office.FileProperties>(Office.context.document.getFilePropertiesAsync);
}

/**
 * Get the classic Word context as an observable.
 * @param logger
 * @returns
 */
export function getClassicWordContext(logger: LoggerService): Observable<Word.RequestContext> {
  return new Observable((observer) => {
    Word.run(async (context: Word.RequestContext) => {
      // load the document properties
      const builtInProperties: Word.DocumentProperties = context.document.properties;
      builtInProperties.load('*');
      context.document.load('*');
      await context.sync();

      observer.next(context);
      observer.complete();
    }).catch((error) => {
      logger.error('[OfficeUtils] Error getting Word context:', error);
      observer.error(error);
    });
  });
}

/**
 * Get an enhanced version of the `Word.RequestContext` type that contains the URL and filename.
 *
 * @returns
 */
export function getWordContext(logger: LoggerService): Observable<WordRequestContext> {
  return forkJoin([getClassicWordContext(logger), getFilePropertiesObservable()]).pipe(
    map(([context, fileProperties]) => {
      Object.assign(context as WordRequestContext, {
        url: fileProperties.url,
        fileName: fileProperties.url?.split('/').pop() ?? '',
      });
      return context as WordRequestContext;
    })
  );
}

/**
 * Downloads the current document as a compressed file (docx, pptx, xlsx) using Office.js API.
 *
 * @returns An Observable that emits the file content as a Blob.
 */
export function getDocumentAsCompressedObservable(): Observable<Blob> {
  return new Observable((observer) => {
    Office.context.document.getFileAsync(Office.FileType.Compressed, { sliceSize: 1048576 }, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const file = result.value;
        const docdataSlices: Uint8Array[] = [];
        let slicesReceived = 0;

        const getSlice = (sliceIndex: number) => {
          file.getSliceAsync(sliceIndex, (sliceResult) => {
            if (sliceResult.status === Office.AsyncResultStatus.Succeeded) {
              docdataSlices[sliceResult.value.index] = sliceResult.value.data;
              slicesReceived++;
              if (slicesReceived === file.sliceCount) {
                file.closeAsync();
                observer.next(mergeSlices(docdataSlices));
                observer.complete();
              } else {
                getSlice(sliceIndex + 1);
              }
            } else {
              file.closeAsync();
              observer.error(new Error(`getSliceAsync Error: ${sliceResult.error.message}`));
            }
          });
        };

        getSlice(0);
      } else {
        observer.error(new Error(`getFileAsync Error: ${result.error.message}`));
      }
    });
  });
}

/**
 * Merges an array of Uint8Array slices into a single Uint8Array.
 * @param slices The array of Uint8Array slices.
 * @returns A Blob containing the merged data.
 */
function mergeSlices(slices: Uint8Array[]): Blob {
  const totalLength = slices.reduce((sum, slice) => sum + slice.length, 0);
  const mergedArray = new Uint8Array(totalLength);
  let offset = 0;

  for (const slice of slices) {
    mergedArray.set(slice, offset);
    offset += slice.length;
  }
  return new Blob([mergedArray]);
}

/**
 * Creates an observable from an Office.js async result.
 * @param asyncCall The async call to wrap.
 * @returns An observable that emits the result of the async call.
 */
export function createObservableFromAsyncResult<T>(asyncCall: (callback: (result: Office.AsyncResult<T>) => void) => void): Observable<T> {
  return new Observable<T>((observer) => {
    try {
      asyncCall((result) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          observer.error(result.error);
        } else {
          observer.next(result.value);
          observer.complete();
        }
      });
    } catch (error) {
      observer.error(error);
    }
  });
}

export type MailItemMode = 'read' | 'compose';

export type OutlookAttContentResult = OutlookContentResult<Office.AttachmentDetails, Office.AttachmentContent | undefined> & {
  success: boolean;
  error?: unknown;
};

export type OutlookContentType = 'eml' | 'att';
export interface OutlookSharedDetail {
  type: OutlookContentType;
  id: string;
}
export type OutlookSharedContentResult = OutlookContentResult<OutlookSharedDetail, Office.AttachmentContent | undefined> & {
  success: boolean;
  error?: unknown;
};

/**
 * Abstract class representing a mail item.
 *
 * @abstract
 */
export abstract class MailItem {
  /**
   * The unique identifier of the mail item.
   * @type {string | null}
   */
  itemId: string | null;

  /**
   * The subject of the mail item.
   * @type {string}
   */
  subject: string;

  /**
   * The date and time when the mail item was created.
   * @type {Date | null}
   */
  dateTimeCreated: Date | null;

  /**
   * The date and time when the mail item was received by the last mail server.
   * @type {Date | null}
   */
  dateTimeReceived: Date | null;

  /**
   * The attachments associated with the mail item.
   * @type {Office.AttachmentDetails[]}
   */
  attachments: Office.AttachmentDetails[];

  /**
   * The name of the file associated with the mail item.
   */
  fileName: string;

  /**
   * The mode of the mail item.
   * @protected
   * @type {MailItemMode}
   */
  protected _mode: MailItemMode;

  /**
   * Creates an instance of MailItem.
   *
   * @param {string | null} itemId - The unique identifier of the mail item.
   * @param {string} subject - The subject of the mail item.
   * @param {Date | null} dateTimeCreated - The date and time when the mail item was created.
   * @param {Date | null} dateTimeReceived - The date and time when the mail item was received by the last mail server.
   * @param {Office.AttachmentDetails[]} attachments - The attachments associated with the mail item.
   * @param {MailItemMode} mode - The mode of the mail item.
   */
  protected constructor(
    itemId: string | null,
    subject: string,
    dateTimeCreated: Date | null,
    dateTimeReceived: Date | null,
    attachments: Office.AttachmentDetails[],
    mode: MailItemMode,
    fileName: string
  ) {
    this.itemId = itemId;
    this.subject = subject;
    this.dateTimeCreated = dateTimeCreated;
    this.dateTimeReceived = dateTimeReceived;
    this.attachments = attachments;
    this.fileName = fileName;
    this._mode = mode;
  }

  /**
   * Gets the mode of the mail item.
   *
   * @returns {MailItemMode} The mode of the mail item.
   */
  get mode(): MailItemMode {
    return this._mode;
  }

  /**
   * Get the mail item as an MIME (EML) string.
   *
   * @abstract
   * @returns {Observable<string>} An observable that emits the EML string of the mail item.
   */
  abstract getItemContents(): Observable<string>;

  /**
   * Get all the shared content in the mail item.
   *
   * @abstract
   * @param {function} callback - A callback function to handle the response.
   * @param {Observable<void>} [cancellationObservable] - An optional observable to cancel the operation.
   * @returns {Observable<OutlookSharedContentResult[]>} An observable that emits the shared content of the mail item.
   */
  abstract fetchAllContent(
    callback: (resp: OutlookSharedContentResult) => void,
    cancellationObservable: Observable<void>
  ): Observable<void>;

  /**
   * Add and Remove categories in the mail item.
   *
   * @abstract
   * @param {string[]} remove - The categories to remove.
   * @param {string[]} add - The categories to add.
   * @returns {Observable<void>} An observable that completes when the categories have been replaced.
   */
  abstract replaceCategories(remove: string[], add: string[]): Observable<void>;

  /**
   * Add an attachment to the mail item.
   *
   * @abstract
   * @param {string} base64 - The base64 encoded content of the attachment.
   * @param {string} name - The name of the attachment.
   * @returns {Observable<string>} An observable that emits the ID of the added attachment.
   */
  abstract addAttachment(base64: string, name: string): Observable<string>;
}

/**
 * OfficeJS version of the MailItem class.
 */
export class OutlookMailItem extends MailItem {
  private contextItem: OutlookItemType;
  private logger: LoggerService;

  constructor(
    itemId: string | null,
    subject: string,
    dateTimeCreated: Date | null,
    dateTimeReceived: Date | null,
    attachments: Office.AttachmentDetails[],
    mode: MailItemMode,
    contextItem: OutlookItemType,
    logger: LoggerService
  ) {
    super(itemId, subject, dateTimeCreated, dateTimeReceived, attachments, mode, createFileNameFromSubject(subject, 'eml'));
    this.contextItem = contextItem;
    this.logger = logger;
  }

  /**
   * Gets the item contents as an observable.
   * @returns An observable that emits the item contents.
   */
  getItemContents(): Observable<string> {
    if (this._mode === 'compose') {
      throw new Error('Cannot get EML for compose mode');
    }
    return getAsFileAsyncObservable(this.contextItem, this.logger).pipe(map((file) => file.value));
  }

  /**
   * Fetches all content from the mail item.
   * @param callback A callback function to handle the response.
   * @param cancellationObservable An observable to signal cancellation.
   * @returns An observable that emits when the content has been fetched.
   */
  fetchAllContent(callback: (resp: OutlookSharedContentResult) => void, cancellationObservable: Observable<void>): Observable<void> {
    const obs: Observable<OutlookSharedContentResult>[] = [
      this.getItemContents().pipe(
        map((content) => ({
          detail: { type: 'eml' as const, id: this.itemId! },
          value: { content, format: 'base64' },
          success: true,
        })),
        catchError((error) =>
          of({
            detail: { type: 'eml' as const, id: this.itemId! },
            success: false,
            error,
            value: undefined,
          })
        )
      ),
    ];
    this.attachments
      .filter((att) => att.attachmentType !== 'cloud')
      .forEach((att) => {
        const detail = { type: 'att' as const, id: att.id };
        const attObs = getAttachmentContentAsyncObservable(this.contextItem, att, this.logger).pipe(
          map((result) => ({ ...result, detail, success: true })),
          catchError((error) => {
            return of({ detail, success: false, error, value: undefined });
          })
        );
        obs.push(attObs);
      });

    return from(obs).pipe(
      mergeMap((obs) => obs, 2),
      takeUntil(cancellationObservable),
      tap((result) => callback(result)),
      toArray(),
      map(() => {})
    );
  }

  /**
   * Replaces categories in the mail item.
   * @param remove Categories to remove.
   * @param add Categories to add.
   * @returns An observable that emits when the categories have been replaced.
   */
  replaceCategories(remove: string[], add: string[]): Observable<void> {
    if (this._mode === 'compose') {
      throw new Error('Cannot replace categories for compose mode');
    }

    // fetch existing categories
    return createObservableFromAsyncResult<Office.CategoryDetails[]>((callback) => {
      return this.contextItem.categories.getAsync(callback);
    }).pipe(
      mergeMap((categories) => {
        const existing = (categories ?? []).map((cat) => cat.displayName);
        // outlook gets very upset if you try to remove categories that aren't there
        // ensure remove are ones that exist and aren't in add
        const filteredRemove = remove.filter((cat) => !add.includes(cat) && existing.includes(cat));
        // ensure add aren't already in existing
        const filteredAdd = add.filter((cat) => !existing.includes(cat));
        return of(null).pipe(
          // Execute sequentially to ensure remove happens before add
          concatMap(() => this.removeCategories(filteredRemove)),
          delay(300),
          concatMap(() => this.addCategories(filteredAdd))
        );
      })
    );
  }

  /**
   * Removes categories from the mail item.
   * @param categories Categories to remove.
   * @returns An observable that emits when the categories have been removed.
   */
  removeCategories(categories: string[]): Observable<void> {
    if (categories.length === 0) {
      return of(undefined);
    }
    return createObservableFromAsyncResult<void>((callback) => {
      this.contextItem.categories.removeAsync(categories, callback);
    });
  }

  /**
   * Adds categories to the mail item.
   * @param categories Categories to add.
   * @returns An observable that emits when the categories have been added.
   */
  addCategories(categories: string[]): Observable<void> {
    if (categories.length === 0) {
      return of(undefined);
    }
    return createObservableFromAsyncResult<void>((callback) => {
      this.contextItem.categories.addAsync(categories, callback);
    });
  }

  /**
   * Adds an attachment to the mail item.
   * @param base64 The base64-encoded content of the attachment.
   * @param name The name of the attachment.
   * @returns An observable that emits the ID of the added attachment.
   */
  addAttachment(base64: string, name: string): Observable<string> {
    return createObservableFromAsyncResult<string>((callback) => {
      this.contextItem.addFileAttachmentFromBase64Async(base64, name, { isInline: false }, callback);
    });
  }

  /**
   * Converts an Outlook item to an OutlookMailItem.
   * @param item The Outlook item to convert.
   * @param logger The logger service to use for logging.
   * @returns An observable that emits the converted OutlookMailItem.
   */
  static fromOutlookItemType(item: OutlookItemType, logger: LoggerService): Observable<OutlookMailItem> {
    // in read mode its easy to convert the item to MailItem
    if (item.getComposeTypeAsync == null) {
      const readItem = item as Office.MessageRead;
      return createObservableFromAsyncResult<string>((callback) => readItem.getAllInternetHeadersAsync(callback)).pipe(
        map((headerString) => {
          const eml = parseEmailHeaders(headerString);
          return new OutlookMailItem(
            item.itemId,
            item.subject,
            item.dateTimeCreated,
            eml?.receivedDate ?? null,
            item.attachments,
            'read',
            item,
            logger
          );
        })
      );
    }

    // otherwise we have to lookup a few calls
    const compItem = item as Office.MessageCompose;
    return forkJoin([
      createObservableFromAsyncResult<string>((callback) => compItem.subject.getAsync(callback)),
      createObservableFromAsyncResult<Office.AttachmentDetailsCompose[]>((callback) => compItem.getAttachmentsAsync(callback)),
    ]).pipe(
      switchMap(([subject, attachments]) => {
        return of(new OutlookMailItem(null, subject, null, null, attachments as Office.AttachmentDetails[], 'compose', item, logger));
      })
    );
  }

  getComposeAsFile() {
    if (this._mode !== 'compose') {
      throw new Error('Cannot convert to eml unless in compose mode');
    }
    return getMessageComposeAsEmlFile(this.contextItem as Office.MessageCompose, this.logger);
  }
}

function parseEmailHeaders(headerString: string): ExtendedReadedEmlJson | null {
  const resp = readEml(headerString, { headersOnly: true });
  if (typeof resp === 'string' || !('date' in resp)) return null;

  const received = resp.headers['Received'];
  if (received) {
    const receivedArray = Array.isArray(received) ? received : [received];
    if (receivedArray.length === 0) return resp;
    const lastReceived = receivedArray[0].split(';').pop()?.trim();
    if (lastReceived) {
      (resp as ExtendedReadedEmlJson).receivedDate = new Date(lastReceived);
    }
  }
  return resp;
}

const loadItemQueue$ = new BehaviorSubject<void>(undefined);

/**
 * Version of the MailItem class that is loaded from an outlook Mail ID.
 * Outlook has restrictions on concurrently opened items so this delays
 * the loading of the item until it is needed.
 */
export class LoadedOutlookMailItem extends MailItem {
  private logger: LoggerService;

  constructor(
    itemId: string,
    subject: string,
    dateTimeCreated: Date | null,
    dateTimeReceived: Date | null,

    attachments: Office.AttachmentDetails[],
    mode: MailItemMode,
    logger: LoggerService
  ) {
    super(itemId, subject, dateTimeCreated, dateTimeReceived, attachments, mode, createFileNameFromSubject(subject, 'eml'));
    this.logger = logger;
  }

  /**
   * Executes an operation on a loaded Office item and ensures the item is unloaded after the operation completes.
   *
   * This utility function loads an Office item (e.g., a message in compose or read mode) by its ID, performs the specified
   * operation on the loaded item, and then unloads the item regardless of whether the operation succeeds or fails.
   *
   * @template T The type of the result returned by the operation.
   * @param itemId The ID of the Office item to load.
   * @param operation A function that takes the loaded Office item as an argument and returns an observable of type `T`.
   *                  This function defines the operation to be performed on the loaded item.
   * @param cancellationObservable An optional observable that can be used to cancel the operation.
   * @returns An observable of type `T` that emits the result of the operation. If the operation fails, the error is rethrown
   *          after ensuring the item is unloaded.
   */
  private static withLoadedItem<T>(
    itemId: string,
    operation: (item: Office.LoadedMessageCompose | Office.LoadedMessageRead) => Observable<T>,
    cancellationObservable: Observable<void> = NEVER,
    logger: LoggerService
  ): Observable<T> {
    // Queue this operation by using the queue subject
    return loadItemQueue$.pipe(
      // Cancel the operation if the cancellation observable emits
      takeUntil(cancellationObservable),
      // Delay each operation slightly to ensure proper queuing
      delay(10),
      // Only take one value to release the queue for the next operation
      take(1),
      // Load the item and perform the operation
      mergeMap(() => {
        return createObservableFromAsyncResult<Office.LoadedMessageCompose | Office.LoadedMessageRead>((callback) =>
          Office.context.mailbox.loadItemByIdAsync(itemId, callback)
        );
      }, 1),
      mergeMap(
        (item) =>
          operation(item).pipe(
            // Unload the item after the operation completes
            finalize(() => {
              item.unloadAsync(() => {
                logger.debug('[OfficeUtils] Unloaded item');
                // Signal that the next operation can proceed
                loadItemQueue$.next(undefined);
              });
            }),
            // Handle errors while still ensuring unload
            catchError((error) => {
              return throwError(() => error);
            })
          ),
        1
      )
    );
  }

  /**
   * Converts an Outlook item ID to a LoadedOutlookMailItem.
   * @param itemId The ID of the Outlook item to load.
   * @param logger The logger service to use for logging.
   * @returns An observable that emits the LoadedOutlookMailItem.
   */
  static fromOutlookItemId(itemId: string, logger: LoggerService): Observable<LoadedOutlookMailItem> {
    return this.withLoadedItem(
      itemId,
      (item) => {
        if ('dateTimeCreated' in item) {
          /*
        // NOTE!!!  OfficeJS bug is preventing this call from working.
        return createObservableFromAsyncResult<string>((callback) => item.getAllInternetHeadersAsync(callback)).pipe(
          map((headerString) => {
            const eml = parseEmailHeaders(headerString);
            return new LoadedOutlookMailItem(itemId, item.subject, item.dateTimeCreated, eml?.receivedDate ?? null,  item.attachments, 'read', logger);
          })
        );
        */
          const mailItem = new LoadedOutlookMailItem(itemId, item.subject, item.dateTimeCreated, null, item.attachments, 'read', logger);
          return of(mailItem);
        } else {
          const composeItem = item as Office.LoadedMessageCompose;
          return forkJoin([
            createObservableFromAsyncResult<string>((callback) => composeItem.subject.getAsync(callback)),
            createObservableFromAsyncResult<Office.AttachmentDetailsCompose[]>((callback) => composeItem.getAttachmentsAsync(callback)),
          ]).pipe(
            map(([subject, attachments]) => {
              return new LoadedOutlookMailItem(itemId, subject, null, null, attachments as Office.AttachmentDetails[], 'compose', logger);
            })
          );
        }
      },
      undefined,
      logger
    );
  }

  /**
   * Gets the contents of the mail item as an EML file.
   * @returns An observable that emits the EML content.
   */
  getItemContents(): Observable<string> {
    if (this._mode === 'compose') {
      throw new Error('Cannot get EML for compose mode');
    }
    return LoadedOutlookMailItem.withLoadedItem(
      this.itemId!,
      (item) => {
        return this._getItemContents(item as Office.LoadedMessageRead);
      },
      undefined,
      this.logger
    );
  }

  /**
   * Gets the contents of the mail item.
   * @param item The Outlook item to get the contents from.
   * @returns An observable that emits the EML content.
   */
  private _getItemContents(item: Office.LoadedMessageRead): Observable<string> {
    return getAsFileAsyncObservable(item, this.logger).pipe(map((file) => file.value));
  }

  fetchAllContent(callback: (resp: OutlookSharedContentResult) => void, cancellationObservable: Observable<void>): Observable<void> {
    if (this._mode === 'compose') {
      throw new Error('Cannot get attachments for compose mode');
    }
    return LoadedOutlookMailItem.withLoadedItem(
      this.itemId!,
      (item) => {
        const obs: Observable<OutlookSharedContentResult>[] = [
          this._getItemContents(item as Office.LoadedMessageRead).pipe(
            map((content) => ({
              detail: { type: 'eml' as const, id: this.itemId! },
              value: { content, format: 'base64' },
              success: true,
            })),
            catchError((error) =>
              of({
                detail: { type: 'eml' as const, id: this.itemId! },
                success: false,
                error,
                value: undefined,
              })
            )
          ),
        ];
        this.attachments
          .filter((att) => att.attachmentType !== 'cloud')
          .forEach((att) => {
            const detail = { type: 'att' as const, id: att.id };
            const attObs = getAttachmentContentAsyncObservable(item as Office.LoadedMessageRead, att, this.logger).pipe(
              map((result) => ({ ...result, detail, success: true })),
              catchError((error) => {
                return of({ detail, success: false, error, value: undefined });
              })
            );
            obs.push(attObs);
          });
        let count = 0;
        return from(obs).pipe(
          mergeMap((o) => o.pipe(tap(() => this.logger.debug(`[OfficeUtils] Obtained content ${++count} of ${obs.length}`))), 2),
          takeUntil(cancellationObservable),
          tap((result) => callback(result)),
          toArray(),
          map(() => {})
        );
      },
      cancellationObservable,
      this.logger
    );
  }

  /**
   * Replaces categories on the mail item.
   * @param remove Categories to remove.
   * @param add Categories to add.
   * @returns An observable that emits when the operation is complete.
   */
  replaceCategories(remove: string[], add: string[]): Observable<void> {
    if (this._mode === 'compose') {
      throw new Error('Cannot replace categories for compose mode');
    }

    return LoadedOutlookMailItem.withLoadedItem(
      this.itemId!,
      (item) => {
        // fetch existing categories
        return createObservableFromAsyncResult<Office.CategoryDetails[]>((callback) => {
          return item.categories.getAsync(callback);
        }).pipe(
          mergeMap((categories) => {
            const existing = (categories ?? []).map((cat) => cat.displayName);
            // outlook gets very upset if you try to remove categories that aren't there
            // ensure remove are ones that exist and aren't in add
            const filteredRemove = remove.filter((cat) => !add.includes(cat) && existing.includes(cat));
            // ensure add aren't already in existing
            const filteredAdd = add.filter((cat) => !existing.includes(cat));
            return of(null).pipe(
              // Execute sequentially to ensure remove happens before add
              concatMap(() => this.removeCategories(filteredRemove, item)),
              delay(300),
              concatMap(() => this.addCategories(filteredAdd, item))
            );
          })
        );
      },
      undefined,
      this.logger
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addAttachment(base64: string, name: string): Observable<string> {
    throw new Error('Method not implemented.');
  }

  private removeCategories(categories: string[], item: Office.LoadedMessageCompose | Office.LoadedMessageRead): Observable<void> {
    if (!categories?.length) {
      return of(undefined);
    }
    return createObservableFromAsyncResult<void>((callback) => {
      item.categories.removeAsync(categories, callback);
    });
  }

  /**
   * Adds categories to the mail item.
   * @param categories Categories to add.
   * @param item The loaded mail item.
   * @returns An observable that emits when the categories have been added.
   */
  private addCategories(categories: string[], item: Office.LoadedMessageCompose | Office.LoadedMessageRead): Observable<void> {
    if (!categories?.length) {
      return of(undefined);
    }
    return createObservableFromAsyncResult<void>((callback) => {
      item.categories.addAsync(categories, callback);
    });
  }
}

/**
 * Abstract base class for mail items loaded from files (MSG, EML)
 */
export abstract class FileBasedMailItem extends MailItem {
  static lastFileNdx = 5000;
  protected logger: LoggerService;

  protected constructor(
    subject: string,
    dateTimeCreated: Date | null,
    attachments: Office.AttachmentDetails[],
    fileName: string,
    logger: LoggerService
  ) {
    super(`fb-${FileBasedMailItem.lastFileNdx++}`, subject, dateTimeCreated, null, attachments, 'read', fileName);
    this.logger = logger;
  }

  /**
   * Creates a FileBasedMailItem from a file.
   * @param file The file to read.
   * @param logger The logger service.
   * @returns An observable that emits the created FileBasedMailItem.
   */
  static fromFile(file: File, logger: LoggerService): Observable<FileBasedMailItem> {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext === 'eml') {
      return EmlMailItem.fromEmlFile(file, logger);
    } else if (ext === 'msg') {
      return MsgMailItem.fromMsgFile(file, logger);
    } else {
      return new Observable((observer) => {
        observer.error(new Error('Unsupported file type'));
      });
    }
  }

  abstract getAttachment(attachment: Office.AttachmentDetails): Observable<OutlookAttContentResult>;

  /**
   * Fetches all content for the mail item.
   * @param callback The callback to invoke with the fetched content.
   * @param cancellationObservable An observable that can be used to cancel the operation.
   * @returns An observable that emits when the operation is complete.
   */
  fetchAllContent(callback: (resp: OutlookSharedContentResult) => void, cancellationObservable: Observable<void>): Observable<void> {
    const obs: Observable<OutlookSharedContentResult>[] = [
      this.getItemContents().pipe(
        map((content) => ({
          detail: { type: 'eml' as const, id: this.itemId! },
          value: { content, format: 'base64' },
          success: true,
        })),
        catchError((error) =>
          of({
            detail: { type: 'eml' as const, id: this.itemId! },
            success: false,
            error,
            value: undefined,
          })
        )
      ),
    ];
    this.attachments
      .filter((att) => att.attachmentType !== 'cloud')
      .forEach((att) => {
        const detail = { type: 'att' as const, id: att.id };
        const attObs = this.getAttachment(att).pipe(
          map((result) => ({ ...result, detail, success: true })),
          catchError((error) => {
            return of({ detail, success: false, error, value: undefined });
          })
        );
        obs.push(attObs);
      });
    return from(obs).pipe(
      mergeMap((o) => o.pipe(tap(() => this.logger.debug('[OfficeUtils] Obtained content'))), 2),
      takeUntil(cancellationObservable),
      tap((result) => callback(result)),
      toArray(),
      map(() => {})
    );
  }

  replaceCategories(): Observable<void> {
    return of(undefined);
  }

  addAttachment(): Observable<string> {
    return new Observable((observer) => {
      observer.error(new Error('Cannot add attachments to file-based mail items'));
    });
  }
}

/**
 * Represents a mail item loaded from an EML file.
 */
export class EmlMailItem extends FileBasedMailItem {
  private buffer: Buffer;
  private emlData: ReadedEmlJson;

  constructor(
    subject: string,
    dateTimeCreated: Date | null,
    attachments: Office.AttachmentDetails[],
    buffer: Buffer,
    emlData: ReadedEmlJson,
    fileName: string,
    logger: LoggerService
  ) {
    super(subject, dateTimeCreated, attachments, fileName, logger);
    this.buffer = buffer;
    this.emlData = emlData;
  }

  /**
   * Creates an EmlMailItem from a file name and buffer.
   * @param fileName The name of the file.
   * @param buffer The buffer containing the file data.
   * @param logger The logger service.
   * @returns An observable that emits the created EmlMailItem.
   */
  static fromNameAndBuffer(fileName: string, buffer: Buffer, logger: LoggerService): Observable<EmlMailItem> {
    return new Observable((observer) => {
      try {
        const resp = readEml(buffer.toString());
        if (typeof resp === 'string' || !('date' in resp)) {
          logger.error('[OfficeUtils] Failed to parse mail EML. Error:', resp);
          observer.error(new Error('Failed to parse mail EML'));
          return;
        }

        const eml = resp as ReadedEmlJson;
        const atts = (eml.attachments ?? [])
          .map((att) => {
            let { name } = att;
            if (!name) return null;

            if (/^message\/rfc822/.test(att.contentType)) {
              // If the attachment is an RFC822 message, we have to parse it to get the name
              const data = typeof att.data === 'string' ? att.data : decode(att.data).toString();
              if (!att.data64) {
                // suppliment the data64 if not present
                att.data = '';
                att.data64 = Buffer.from(data).toString('base64');
              }
              const resp = readEml(data, { headersOnly: true });
              if (typeof resp === 'object' && 'subject' in resp) {
                const attEml = resp as ReadedEmlJson;
                name = createFileNameFromSubject(attEml.subject || 'Unknown', 'eml');
                att.name = name;
              }
            }

            // emls do not have a unique ID for attachments, so we make up one and record the index offset
            return {
              id: crypto.randomUUID(),
              name,
              size: att.data?.length || 0,
              attachmentType: 'file',
              contentType: att.contentType || '',
              isInline: !!att.inline,
            } as Office.AttachmentDetails;
          })
          .filter((att) => att !== null) as Office.AttachmentDetails[];

        const mailItem = new EmlMailItem(eml.subject || '', new Date(eml.date || ''), atts, buffer, eml, fileName, logger);

        observer.next(mailItem);
        observer.complete();
      } catch (err) {
        observer.error(new Error('Failed to parse EML content: ' + getErrorMessage(err)));
      }
    });
  }

  /**
   * Creates an EmlMailItem from a file.
   * @param file The file to read.
   * @param logger The logger service.
   * @returns An observable that emits the created EmlMailItem.
   */
  static fromEmlFile(file: File, logger: LoggerService): Observable<EmlMailItem> {
    return convertFileToBuffer(file).pipe(mergeMap((buffer) => EmlMailItem.fromNameAndBuffer(file.name, buffer, logger)));
  }

  /**
   * Gets the contents of the mail item.
   * @returns An observable that emits the mail item contents.
   */
  getItemContents(): Observable<string> {
    return of(stripUnsentHeader(this.buffer.toString('base64')));
  }

  /**
   * Gets the attachment content.
   * @param attachment The attachment details.
   * @returns An observable that emits the attachment content.
   */
  getAttachment(attachment: Office.AttachmentDetails): Observable<OutlookAttContentResult> {
    return of(attachment).pipe(
      map((att) => {
        const emlAtt = this.emlData.attachments?.find((a) => a.id === att.id || a.name === att.name);
        if (!emlAtt || !emlAtt.data64) {
          return {
            detail: att,
            success: false,
            error: new Error('Attachment not found or empty'),
            value: undefined,
          };
        }
        const isNestedEml = /^message\/rfc822/i.test(emlAtt.contentType) || /\.eml$/i.test(att.name);
        return {
          detail: att,
          success: true,
          value: {
            content: isNestedEml ? stripUnsentHeader(emlAtt.data64) : emlAtt.data64,
            contentType: emlAtt.contentType,
            format: 'base64',
          },
        };
      })
    );
  }
}

/**
 * Represents a mail item loaded from an MSG file.
 */
export class MsgMailItem extends FileBasedMailItem {
  private msgContent: Buffer;
  private msgReader: MsgReader;
  private attachmentIndex: { [key: string]: number };

  constructor(
    subject: string,
    dateTimeCreated: Date | null,
    attachments: Office.AttachmentDetails[],
    msgContent: Buffer,
    msgReader: MsgReader,
    fileName: string,
    attachmentIndex: { [key: string]: number },
    logger: LoggerService
  ) {
    super(subject, dateTimeCreated, attachments, fileName, logger);
    this.msgContent = msgContent;
    this.msgReader = msgReader;
    this.fileName = fileName;
    this.attachmentIndex = attachmentIndex;
  }

  /**
   * Creates a MsgMailItem from a file.
   * @param file The file to read.
   * @param logger The logger service.
   * @returns An observable that emits the created MsgMailItem.
   */
  static fromMsgFile(file: File, logger: LoggerService): Observable<MsgMailItem> {
    return convertFileToBuffer(file).pipe(mergeMap((buffer) => MsgMailItem.fromNameAndBuffer(file.name, buffer, logger)));
  }

  /**
   * Creates a MsgMailItem from a file.
   * @param fileName The name of the file.
   * @param buffer The file content as a buffer.
   * @param logger The logger service.
   * @returns An observable that emits the created MsgMailItem.
   */
  static fromNameAndBuffer(fileName: string, buffer: Buffer, logger: LoggerService): Observable<MsgMailItem> {
    return new Observable((observer) => {
      try {
        const msgReader = new MsgReader(new DataView(buffer.buffer, buffer.byteOffset, buffer.byteLength));
        const msgData = msgReader.getFileData();
        const atts = msgData.attachments || [];
        const attachmentIndex: { [key: string]: number } = {};

        const mailItem = new MsgMailItem(
          stripTrim(msgData.subject) || '',
          new Date(msgData.creationTime || ''),
          atts.map((att, index) => {
            let name = stripTrim(att.fileName) || 'Unknown';
            if (att.innerMsgContent && att.innerMsgContentFields) {
              const dt = att.innerMsgContentFields?.dataType?.trim() || 'attachment';
              if (dt === 'msg') {
                name = createFileNameFromSubject(stripTrim(att.innerMsgContentFields.subject) || 'Unknown', 'msg');
              } else if (dt === 'attachment') {
                name = att.innerMsgContentFields?.fileName || 'Unknown';
              }
            }

            const id = crypto.randomUUID();
            attachmentIndex[id] = index;

            return {
              id,
              name,
              size: att.contentLength || 0,
              attachmentType: 'file',
              contentType: att.attachMimeTag || '',
            };
          }) as Office.AttachmentDetails[],
          buffer,
          msgReader,
          fileName,
          attachmentIndex,
          logger
        );

        observer.next(mailItem);
        observer.complete();
      } catch (err) {
        observer.error(new Error('Failed to parse MSG content: ' + getErrorMessage(err)));
      }
    });
  }

  /**
   * Gets the contents of the mail item.
   * @returns An observable that emits the mail item contents.
   */
  getItemContents(): Observable<string> {
    return of(this.msgContent.toString('base64'));
  }

  /**
   * Gets the attachment content.
   * @param attachment The attachment details.
   * @returns An observable that emits the attachment content.
   */
  getAttachment(attachment: Office.AttachmentDetails): Observable<OutlookAttContentResult> {
    return of(attachment).pipe(
      map((att) => {
        // Find attachment by converting ID back to number for comparison
        const index = this.attachmentIndex[att.id];
        const attachmentData = this.msgReader.getAttachment(index);
        if (!attachmentData) {
          return {
            detail: att,
            success: false,
            error: new Error('Attachment not found or empty'),
            value: undefined,
          };
        }

        // Convert Uint8Array to base64
        const content = Buffer.from(attachmentData.content).toString('base64');

        return {
          detail: att,
          success: true,
          value: {
            content,
            contentType: '',
            format: 'base64',
          },
        };
      })
    );
  }
}

/**
 * Gets the error message from an error object.
 * @param error The error object.
 * @returns The error message.
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

/**
 * Creates a file name from the email subject and extension.
 * @param subject The email subject.
 * @param ext The file extension.
 * @returns The created file name.
 */
function createFileNameFromSubject(subject: string, ext: 'msg' | 'eml'): string {
  return cleanAndTruncateFileName(`${subject}.${ext}`, MAX_FILENAME_LENGTH, false, true, false);
}

/**
 * Strips whitespace and control characters from a string.
 * @param str The input string.
 * @returns The stripped string.
 */

function stripTrim(str: string | undefined): string | undefined {
  // eslint-disable-next-line no-control-regex
  return str?.replace(/[\x00-\x1F\x7F]/g, '').trim();
}

/**
 * Extracts all Content-ID references from HTML body.
 * Searches for cid: references in src attributes, CSS backgrounds, and other locations.
 * @param htmlBody The HTML body content.
 * @returns A Set of Content-IDs found in the HTML.
 */
function extractCidReferences(htmlBody: string): Set<string> {
  const cidSet = new Set<string>();

  // Match cid: in various contexts:
  // - src="cid:..."
  // - url('cid:...')
  // - url("cid:...")
  // - background:url(cid:...)
  const cidPattern = /cid:([^"'\s>)]+)/gi;

  let match;
  while ((match = cidPattern.exec(htmlBody)) !== null) {
    const cid = match[1].trim();
    if (cid) {
      cidSet.add(cid);
    }
  }

  return cidSet;
}

/**
 * Maps attachments to their Content-IDs based on available information.
 * For Outlook Web: uses the contentId property if available.
 * For Outlook Classic: attempts to match filename patterns to CIDs found in HTML.
 * @param attachmentContents The attachment contents.
 * @param cidReferences CID references found in the HTML body.
 * @param logger The logger service.
 * @returns A Map of attachment ID to Content-ID.
 */
function mapAttachmentsToContentIds(
  attachmentContents: Array<{ detail: Office.AttachmentDetailsCompose; content: Office.AttachmentContent }>,
  cidReferences: Set<string>,
  logger: LoggerService
): Map<string, string> {
  const contentIdMap = new Map<string, string>();

  for (const attContent of attachmentContents) {
    if (!attContent || !attContent.detail.isInline) {
      continue;
    }

    const detail = attContent.detail as any;
    let contentId: string | null = null;

    // Case 1: Outlook Web - has contentId property
    if (detail.contentId) {
      contentId = detail.contentId;
      logger.debug('[OfficeUtils] Using contentId from Outlook Web:', {
        name: detail.name,
        contentId: contentId,
      });
    }
    // Case 2: Outlook Classic - try to match filename to CID references
    else {
      // Look for CID that contains the filename (e.g., "image001.png@01DC99D2.3D83EC30" contains "image001.png")
      const filename = detail.name;
      for (const cid of cidReferences) {
        if (cid.startsWith(filename) || cid.includes(filename)) {
          contentId = cid;
          logger.debug('[OfficeUtils] Matched filename to CID from HTML:', {
            name: filename,
            contentId: contentId,
          });
          break;
        }
      }

      // If no match found, generate a Content-ID based on filename
      if (!contentId) {
        // Generate a Content-ID that mimics Outlook Classic format
        contentId = `${filename}@${crypto.randomUUID().substring(0, 18).replace(/-/g, '').toUpperCase()}`;
        logger.warn('[OfficeUtils] No CID match found, generated Content-ID:', {
          name: filename,
          contentId: contentId,
          note: 'This may not match HTML references - inline image may not display',
        });
      }
    }

    if (contentId) {
      contentIdMap.set(detail.id, contentId);
    }
  }

  return contentIdMap;
}

/**
 * Wraps a base64 string into lines of specified length (RFC 2045 compliant).
 * @param base64String The base64 string to wrap.
 * @param lineLength The maximum line length (default 76 per RFC 2045).
 * @returns The wrapped base64 string with CRLF line breaks.
 */
function wrapBase64(base64String: string, lineLength: number = 76): string {
  const lines: string[] = [];
  for (let i = 0; i < base64String.length; i += lineLength) {
    lines.push(base64String.substring(i, i + lineLength));
  }
  return lines.join('\r\n');
}

function getMessageComposeAsEmlFile(item: Office.MessageCompose, logger: LoggerService): Observable<File> {
  // First, save the draft to ensure all data is synchronized
  return createObservableFromAsyncResult<string>((callback) => {
    item.saveAsync(callback);
  }).pipe(
    // Now gather all the message data in parallel
    switchMap(() => {
      return forkJoin({
        subject: createObservableFromAsyncResult<string>((callback) => item.subject.getAsync(callback)),
        from: createObservableFromAsyncResult<Office.EmailAddressDetails>((callback) => item.from.getAsync(callback)),
        to: createObservableFromAsyncResult<Office.EmailAddressDetails[]>((callback) => item.to.getAsync(callback)),
        cc: createObservableFromAsyncResult<Office.EmailAddressDetails[]>((callback) => item.cc.getAsync(callback)),
        bcc: createObservableFromAsyncResult<Office.EmailAddressDetails[]>((callback) => item.bcc.getAsync(callback)),
        htmlBody: createObservableFromAsyncResult<string>((callback) => item.body.getAsync(Office.CoercionType.Html, callback)),
        attachments: createObservableFromAsyncResult<Office.AttachmentDetailsCompose[]>((callback) => item.getAttachmentsAsync(callback)),
      });
    }),
    // Fetch attachment contents in parallel (if any)
    switchMap((messageData) => {
      const attachmentObs$ = messageData.attachments
        .filter((att) => att.attachmentType !== 'cloud')
        .map((att) =>
          getAttachmentContentAsyncObservable(item as OutlookItemType, att as Office.AttachmentDetails, logger).pipe(
            map((result) => ({ detail: att, content: result.value })),
            catchError((error) => {
              logger.warn(`[OfficeUtils] Failed to get attachment ${att.name}:`, error);
              return of(null);
            })
          )
        );

      if (attachmentObs$.length === 0) {
        return of({ ...messageData, attachmentContents: [] });
      }

      return forkJoin(attachmentObs$).pipe(
        map((attachmentContents) => ({
          ...messageData,
          attachmentContents: attachmentContents.filter((a) => a !== null),
        }))
      );
    }),
    // Build the EML using mimetext
    map((data) => {
      const msg = createMimeMessage();

      // Set headers
      msg.setSender({ name: data.from.displayName, addr: data.from.emailAddress });

      if (data.to.length > 0) {
        msg.setRecipients(data.to.map((r) => ({ name: r.displayName, addr: r.emailAddress })));
      }

      if (data.cc.length > 0) {
        msg.setRecipients(
          data.cc.map((r) => ({ name: r.displayName, addr: r.emailAddress })),
          { type: 'Cc' }
        );
      }

      if (data.bcc.length > 0) {
        msg.setRecipients(
          data.bcc.map((r) => ({ name: r.displayName, addr: r.emailAddress })),
          { type: 'Bcc' }
        );
      }

      msg.setSubject(data.subject || ' ');
      msg.setHeader('Date', new Date().toUTCString());
      msg.setHeader('Message-ID', `<${crypto.randomUUID()}@ftf-outlook-addin>`);

      // Extract all CID references from HTML body (including CSS backgrounds, src attributes, etc.)
      const cidReferences = extractCidReferences(data.htmlBody || '');
      logger.debug('[OfficeUtils] Found CID references in HTML:', Array.from(cidReferences));

      // Build a mapping of attachment details to their Content-IDs
      const attachmentContentIds = mapAttachmentsToContentIds(data.attachmentContents, cidReferences, logger);

      // Separate inline and regular attachments
      const inlineAttachments = data.attachmentContents.filter((att) => att && att.detail.isInline);
      const regularAttachments = data.attachmentContents.filter((att) => att && !att.detail.isInline);

      // Add body first
      if (data.htmlBody) {
        msg.addMessage({
          contentType: 'text/html',
          data: wrapBase64(Buffer.from(data.htmlBody || '', 'utf-8').toString('base64')),
          encoding: 'base64',
        });
      }

      // Add inline attachments second (they will be grouped with the body in multipart/related)
      for (const attContent of inlineAttachments) {
        if (attContent && attContent.content) {
          const contentId = attachmentContentIds.get(attContent.detail.id);
          const contentType = lookupMimeType(attContent.detail.name) || 'application/octet-stream';

          msg.addAttachment({
            filename: attContent.detail.name,
            contentType: contentType,
            data: wrapBase64(attContent.content.content),
            inline: true,
            headers: {
              'Content-ID': `<${contentId}>`,
            },
            encoding: 'base64',
          });

          logger.debug('[OfficeUtils] Adding inline attachment with Content-ID:', {
            name: attContent.detail.name,
            contentId: contentId,
          });
        }
      }

      // Add regular attachments last (they will be at the multipart/mixed level)
      for (const attContent of regularAttachments) {
        if (attContent && attContent.content) {
          const contentType = lookupMimeType(attContent.detail.name) || 'application/octet-stream';

          msg.addAttachment({
            filename: attContent.detail.name,
            contentType: contentType,
            data: wrapBase64(attContent.content.content),
            encoding: 'base64',
          });
        }
      }

      // Generate EML string
      const emlString = msg.asRaw();
      const emlBuffer = Buffer.from(emlString, 'utf-8');

      // return File object
      return new File([emlBuffer], cleanupFilename((data.subject || 'Untitled') + '.eml'));
    })
  );
}
